import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../../store/useAppStore';
import { Report, ReportType, ProjectDetails, ProjectWorkflowStatus } from '../../types';
import { Briefcase, Search, Filter, FolderOpen, MapPin, Calendar, User, ChevronDown } from 'lucide-react';

// Helper function for status badges
const getStatusBadge = (status?: ProjectWorkflowStatus) => {
    switch (status) {
        case ProjectWorkflowStatus.PendingTeamAcceptance:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">بانتظار موافقة الفريق</span>;
        case ProjectWorkflowStatus.InProgress:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500 border border-blue-500/20">قيد التنفيذ</span>;
        case ProjectWorkflowStatus.FinishingWorks:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">أعمال التشطيبات</span>;
        case ProjectWorkflowStatus.ConcreteWorksDone:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-500 border border-orange-500/20">بانتظار الدفعة الثانية</span>;
        case ProjectWorkflowStatus.TechnicallyCompleted:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">مكتمل فنياً</span>;
        case ProjectWorkflowStatus.Finalized:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">مكتمل</span>;
        default:
            return <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-500 border border-slate-500/20">مسودة</span>;
    }
}

const PROJECT_STAGES = [
    { id: 'contract', name: 'العقد' },
    { id: 'firstPayment', name: 'الدفعة الأولى' },
    { id: 'notifyTeam', name: 'إشعار الفريق' },
    { id: 'concreteWorks', name: 'الأعمال الخرسانية' },
    { id: 'secondPayment', name: 'الدفعة الثانية' },
    { id: 'installationComplete', name: 'التركيب' },
    { id: 'deliveryHandover', name: 'التسليم' },
];

const ModernAdminProjectCard: React.FC<{ report: Report; index: number }> = ({ report, index }) => {
    const navigate = useNavigate();
    const details = report.details as ProjectDetails;

    // Calculate current stage index
    const reversedUpdates = [...details.updates].reverse();
    const lastCompletedUpdate = reversedUpdates.find(u => u.completed);
    const currentStageIndex = lastCompletedUpdate
        ? PROJECT_STAGES.findIndex(s => s.id === lastCompletedUpdate.id)
        : -1;

    // Calculate percentage
    const progressPercentage = currentStageIndex >= 0
        ? Math.round(((currentStageIndex + 1) / PROJECT_STAGES.length) * 100)
        : 0;

    return (
        <div
            onClick={() => navigate(`/reports/${report.id}`)}
            className="group relative glass-card p-5 rounded-2xl hover:bg-slate-800/50 transition-all duration-300 cursor-pointer border border-slate-700/50 hover:border-blue-500/30 overflow-hidden animate-fade-in-up"
            style={{ animationDelay: `${index * 50}ms` }}
        >
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="flex justify-between items-start mb-4 pl-2">
                <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                        <Briefcase size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-lg leading-tight mb-1 group-hover:text-blue-400 transition-colors">{details.projectOwner}</h3>
                        <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                            <span>{report.id}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                            <span className="flex items-center gap-1 text-slate-300"><User size={10} /> {report.employeeName}</span>
                        </div>
                    </div>
                </div>
                {getStatusBadge(report.projectWorkflowStatus)}
            </div>

            <div className="space-y-4 mb-4">
                <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-slate-400">المرحلة الحالية</span>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-blue-400">
                                {currentStageIndex >= 0 ? PROJECT_STAGES[currentStageIndex].name : 'لم يبدأ'}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-mono">
                                {progressPercentage}%
                            </span>
                        </div>
                    </div>

                    {/* 7-Stage Progress Indicator */}
                    <div className="flex gap-1.5 mt-2" dir="ltr">
                        {PROJECT_STAGES.map((stage, idx) => {
                            const isCompleted = currentStageIndex >= idx;
                            const isCurrent = currentStageIndex === idx;

                            let bgClass = 'bg-slate-700';
                            if (isCompleted) bgClass = 'bg-blue-500';
                            if (isCurrent) bgClass = 'bg-blue-400 animate-pulse';

                            return (
                                <div
                                    key={stage.id}
                                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${bgClass}`}
                                    title={stage.name}
                                />
                            );
                        })}
                    </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-800/30 p-2 rounded-lg border border-slate-700/30">
                    <MapPin size={12} className="text-slate-500" />
                    <span className="truncate">{details.location}</span>
                    <span className="mx-1 text-slate-600">|</span>
                    <Calendar size={12} className="text-slate-500" />
                    <span>{new Date(details.startDate).toLocaleDateString('ar-SA')}</span>
                </div>
            </div>
        </div>
    );
};

const ModernAdminProjectReportsScreen: React.FC = () => {
    const { reports } = useAppStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const projectReports = useMemo(() =>
        reports
            .filter(r => r.type === ReportType.Project)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
        [reports]);

    const filteredReports = useMemo(() => {
        return projectReports.filter(report => {
            const searchLower = searchTerm.toLowerCase();
            const searchMatch = searchLower === '' ||
                (report.employeeName || '').toLowerCase().includes(searchLower) ||
                (report.details.projectOwner || '').toLowerCase().includes(searchLower) ||
                (report.id || '').toLowerCase().includes(searchLower);

            const statusMatch = statusFilter === 'all' || report.projectWorkflowStatus === statusFilter;

            return searchMatch && statusMatch;
        });
    }, [projectReports, searchTerm, statusFilter]);

    return (
        <div className="space-y-8 animate-contentFadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text flex items-center gap-3">
                        <Briefcase className="text-cyan-400" size={32} />
                        تقارير المشاريع
                    </h1>
                    <p className="text-slate-400 mt-1">متابعة وإدارة سير عمل المشاريع وتقاريرها</p>
                </div>
                <div className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 flex items-center gap-2">
                    <FolderOpen size={18} className="text-cyan-400" />
                    <span>إجمالي التقارير: {projectReports.length}</span>
                </div>
            </div>

            {/* Filters */}
            <div className="glass-card p-4 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            placeholder="بحث باسم الموظف، مالك المشروع، أو رقم التقرير..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 pr-10 pl-4 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 no-scrollbar">
                        <button
                            onClick={() => setStatusFilter('all')}
                            className={`
                                flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all border
                                ${statusFilter === 'all'
                                    ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.15)]'
                                    : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                                }
                            `}
                        >
                            <Filter size={14} />
                            <span>كل الحالات</span>
                        </button>
                        {Object.entries(ProjectWorkflowStatus).map(([key, value]) => (
                            <button
                                key={key}
                                onClick={() => setStatusFilter(value)}
                                className={`
                                    flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all border
                                    ${statusFilter === value
                                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.15)]'
                                        : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                                    }
                                `}
                            >
                                <span>{value}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Reports Grid */}
            {filteredReports.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredReports.map((report, idx) => (
                        <ModernAdminProjectCard key={report.id} report={report} index={idx} />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center glass-panel rounded-2xl border border-white/5">
                    <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
                        <Briefcase size={40} className="text-slate-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-300">لا توجد تقارير مشاريع</h3>
                    <p className="text-slate-500 mt-2">لم يتم العثور على تقارير مشاريع تطابق الفلاتر الحالية.</p>
                    <button
                        onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                        className="mt-6 text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                        مسح الفلاتر
                    </button>
                </div>
            )}
        </div>
    );
};

export default ModernAdminProjectReportsScreen;
